export enum TargetingFormSection {
  Content = 'CONTENT',
  DeviceTypes = 'DEVICE_TYPES',
  Platforms = 'PLATFORMS',
  Bundles = 'BUNDLES',
  IpTargeting = 'IP_TARGETING',
  SpecialHandling = 'SPECIAL_HANDLING',
  Performance = 'PERFORMANCE',
  Languages = 'LANGUAGES',
  Geolocations = 'GEOLOCATIONS',
  ZipCodes = 'ZIP_CODES',
  ZipCodesImport = 'ZIP_CODES_IMPORT',
  IpAddressesImport = 'IP_ADDRESSES_IMPORT',
  SupplyCompanies = 'SUPPLY_COMPANIES',
  Supply = 'SUPPLY',
  LiveRamp = 'LIVE_RAMP',
  Placements = 'PLACEMENTS',
  Domains = 'DOMAINS',
  TailoredSupply = 'TAILORED_SUPPLY',
  Contextual = 'CONTEXTUAL',
  PmpDealIds = 'PMP_DEAL_IDS',
  ExtraParameters = 'EXTRA_PARAMETERS',
}

export enum TargetingConsumer {
  Standalone = 'STANDALONE',
  LineItem = 'LINE_ITEM',
  DemandConnection = 'DEMAND_CONNECTION',
}

export type TargetingFormConfig = Readonly<{
  consumer: TargetingConsumer;
  sectionOverrides?: Readonly<Partial<Record<TargetingFormSection, boolean>>>;
}>;

type TargetingSectionVisibility = Readonly<{
  section: TargetingFormSection;
  hideFor: readonly TargetingConsumer[];
}>;

const targetingFormSectionVisibility: readonly TargetingSectionVisibility[] = [
  {
    section: TargetingFormSection.Content,
    hideFor: [TargetingConsumer.DemandConnection],
  },
  {
    section: TargetingFormSection.Platforms,
    hideFor: [TargetingConsumer.LineItem],
  },
  {
    section: TargetingFormSection.Bundles,
    hideFor: [TargetingConsumer.LineItem],
  },
  {
    section: TargetingFormSection.IpTargeting,
    hideFor: [TargetingConsumer.LineItem],
  },
  { section: TargetingFormSection.DeviceTypes, hideFor: [] },
  {
    section: TargetingFormSection.SpecialHandling,
    hideFor: [TargetingConsumer.LineItem],
  },
  {
    section: TargetingFormSection.Performance,
    hideFor: [TargetingConsumer.Standalone, TargetingConsumer.DemandConnection],
  },
  {
    section: TargetingFormSection.Languages,
    hideFor: [TargetingConsumer.Standalone, TargetingConsumer.DemandConnection],
  },
  { section: TargetingFormSection.Geolocations, hideFor: [] },
  {
    section: TargetingFormSection.ZipCodes,
    hideFor: [TargetingConsumer.LineItem, TargetingConsumer.DemandConnection],
  },
  {
    // Line items replace saved ZIP/IP collections through immediate CSV imports.
    section: TargetingFormSection.ZipCodesImport,
    hideFor: [TargetingConsumer.Standalone, TargetingConsumer.DemandConnection],
  },
  {
    section: TargetingFormSection.IpAddressesImport,
    hideFor: [TargetingConsumer.Standalone, TargetingConsumer.DemandConnection],
  },
  {
    section: TargetingFormSection.SupplyCompanies,
    hideFor: [TargetingConsumer.Standalone, TargetingConsumer.DemandConnection],
  },
  { section: TargetingFormSection.Supply, hideFor: [] },
  {
    section: TargetingFormSection.PmpDealIds,
    hideFor: [TargetingConsumer.LineItem],
  },
  {
    section: TargetingFormSection.LiveRamp,
    hideFor: [TargetingConsumer.LineItem],
  },
  {
    section: TargetingFormSection.Placements,
    hideFor: [TargetingConsumer.LineItem, TargetingConsumer.DemandConnection],
  },
  {
    section: TargetingFormSection.Domains,
    hideFor: [TargetingConsumer.LineItem],
  },
  {
    section: TargetingFormSection.TailoredSupply,
    hideFor: [TargetingConsumer.LineItem],
  },
  {
    section: TargetingFormSection.Contextual,
    hideFor: [TargetingConsumer.Standalone, TargetingConsumer.DemandConnection],
  },
  {
    section: TargetingFormSection.ExtraParameters,
    hideFor: [TargetingConsumer.LineItem],
  },
];

export const standaloneTargetingConfig: TargetingFormConfig = {
  consumer: TargetingConsumer.Standalone,
};

export const lineItemTargetingConfig: TargetingFormConfig = {
  consumer: TargetingConsumer.LineItem,
};

export const demandConnectionTargetingConfig: TargetingFormConfig = {
  consumer: TargetingConsumer.DemandConnection,
};

export function resolveTargetingFormSections(config: TargetingFormConfig) {
  // This collection owns section visibility only. TargetingForm JSX owns presentation order.
  return targetingFormSectionVisibility
    .filter(({ section, hideFor }) => {
      const override = config.sectionOverrides?.[section];
      return override ?? !hideFor.includes(config.consumer);
    })
    .map(({ section }) => section);
}

export function targetingSectionIsVisible(
  sections: readonly TargetingFormSection[],
  section: TargetingFormSection,
) {
  return sections.includes(section);
}
